import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { formatKyrgyzCurrency } from '@/lib/i18n';
import { TFunction } from 'i18next';
import {
  LayoutDashboard,
  DollarSign,
  ShoppingBag,
  AlertTriangle,
  BarChart3,
  Target,
  Shield,
  Activity,
  ChefHat,
  Bell,
  Monitor,
  Users,
  Smartphone
} from 'lucide-react';

import { KitchenMonitorWidget } from './KitchenMonitorWidget';
import { useAdminStore } from '@/stores/adminStore';
import { useEmployeeStore } from '@/stores/employeeStore';
import { useKitchenAnalyticsStore } from '@/stores/kitchenAnalyticsStore';
import { cn } from '@/utils';

// Quick stats card component
const QuickStatsCard = ({
  title,
  value,
  unit,
  change,
  changeType,
  icon: Icon,
  color = 'blue',
  onClick
}: {
  title: string;
  value: string | number;
  unit?: string;
  change?: string;
  changeType?: 'positive' | 'negative' | 'neutral';
  /** Optional: a card with no honest glyph shows none. See the revenue card. */
  icon?: React.ComponentType<{ className?: string }>;
  color?: 'blue' | 'green' | 'orange' | 'red' | 'purple' | 'gray';
  onClick?: () => void;
}) => {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200 text-blue-600',
    green: 'bg-green-50 border-green-200 text-green-600',
    orange: 'bg-orange-50 border-orange-200 text-orange-600',
    red: 'bg-red-50 border-red-200 text-red-600',
    purple: 'bg-purple-50 border-purple-200 text-purple-600',
    gray: 'bg-gray-50 border-gray-200 text-gray-600',
  };

  const changeTypeClasses = {
    positive: 'text-green-600',
    negative: 'text-red-600',
    neutral: 'text-gray-600',
  };

  return (
    <motion.div
      whileHover={{ y: -2 }}
      className={cn(
        'rounded-xl border-2 p-4 transition-all cursor-pointer hover:shadow-md',
        colorClasses[color],
        onClick && 'hover:shadow-lg'
      )}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-3">
        {/* Iconless cards keep an icon-height spacer: it holds `change` to the
            right and keeps this row 24px tall, so the value and label below
            still line up with the icon-bearing cards beside them. */}
        {Icon ? <Icon className="w-6 h-6" /> : <span className="h-6" aria-hidden="true" />}
        {change && (
          <span className={cn('text-sm font-medium', changeTypeClasses[changeType!])}>
            {change}
          </span>
        )}
      </div>
      
      <div className="space-y-1">
        <div className="flex items-baseline space-x-2">
          <span className="text-2xl font-semibold tracking-tight text-gray-900">{value}</span>
          {unit && <span className="text-sm text-gray-600">{unit}</span>}
        </div>
        <div className="text-sm font-medium text-gray-700">{title}</div>
      </div>
    </motion.div>
  );
};

// Recent activity feed
const RecentActivityFeed = ({ t }: { t: TFunction }) => {
  const { auditLogs } = useAdminStore();
  const recentLogs = auditLogs.slice(0, 5);

  const getActivityIcon = (action: string) => {
    if (action.includes('ORDER')) return ShoppingBag;
    if (action.includes('STAFF')) return Users;
    if (action.includes('REFUND')) return DollarSign;
    if (action.includes('KITCHEN')) return ChefHat;
    return Activity;
  };

  const getActivityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-600';
      case 'high': return 'text-orange-600';
      case 'medium': return 'text-yellow-600';
      default: return 'text-blue-600';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-soft border border-neutral-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Activity className="w-5 h-5 mr-2 text-blue-500" />
          {t('dashboard.recentActivity')}
        </h3>
        <span className="text-sm text-gray-500">{t('admin.dashboard.recentActivity.recent_plural', { count: recentLogs.length })}</span>
      </div>

      <div className="space-y-3 max-h-64 overflow-y-auto">
        {recentLogs.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">{t('admin.dashboard.recentActivity.noActivity')}</p>
        ) : (
            recentLogs.map((log) => {
                const Icon = getActivityIcon(log.action);
                return (
                    <div key={log.id} className="flex items-start space-x-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                    <Icon className={cn('w-4 h-4 mt-0.5 flex-shrink-0', getActivityColor(log.severity))} />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                        {log.action.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase())}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                        {log.adminName} • {log.timestamp.toLocaleTimeString()}
                        </div>
                    </div>
                    </div>
                );
            })
        )}
      </div>
    </div>
  );
};

// The "Состояние системы" panel used to live here: Общее 94%, Кухня 89%,
// Касса 97%, Сеть 98%, База Данных 92%, under a pulsing green "Работает" dot.
// Every one of those numbers was a `useState` literal — the panel polled
// nothing, subscribed to nothing, and would have kept reporting 94% healthy
// with the kitchen offline and the database down. There is no health-check
// endpoint to wire it to, and a status board that cannot go red is worse than
// no status board on the screen that sells the product, so it is gone. If it
// comes back, it comes back reading real probes.

// Performance alerts
const PerformanceAlerts = ({ t }: { t: TFunction }) => {
  const { fraudAlerts = [] } = useAdminStore();
  const { bottleneckAlerts = [] } = useKitchenAnalyticsStore();
  const activeAlerts = [
    ...(fraudAlerts || []).filter(a => a.status === 'new'),
    ...(bottleneckAlerts || []).filter(a => !a.resolved)
  ].slice(0, 3);

  // An empty alert list is not an all-clear. Nothing in the app calls
  // addFraudAlert or addBottleneckAlert — the two lists behind this panel are
  // append-only and no code appends — so "Все системы работают гладко /
  // Проблем не обнаружено" was a green reassurance from a detector that does
  // not exist. Reporting "no monitoring data" is the honest version, and it
  // stops looking healthy when it is merely blind.
  if (activeAlerts.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-soft border border-neutral-200 p-6">
        <div className="flex items-center text-gray-700">
          <Bell className="w-5 h-5 mr-2 text-gray-400 flex-shrink-0" />
          <span className="font-medium">{t('admin.dashboard.activeAlerts.title')}</span>
        </div>
        <p className="text-sm text-gray-500 mt-2">
          {t('admin.dashboard.activeAlerts.noMonitoring')}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-soft border border-neutral-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Bell className="w-5 h-5 mr-2 text-orange-500" />
          {t('admin.dashboard.activeAlerts.title')}
        </h3>
        <span className="text-sm text-orange-600 font-medium">{t('admin.dashboard.activeAlerts.active_plural', { count: activeAlerts.length })}</span>
      </div>

      <div className="space-y-3">
        {activeAlerts.map((alert) => (
          <div key={alert.id} className="p-3 rounded-lg border border-orange-200 bg-orange-50">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="text-sm font-medium text-orange-900">
                  {alert.description?.startsWith('admin.') ? t(alert.description) : alert.description || `${alert.type?.replace(/_/g, ' ')} Alert`}
                </div>
                <div className="text-xs text-orange-700 mt-1">
                  {t(`admin.dashboard.activeAlerts.priority.${alert.severity || alert.type}`)} • 
                  {t('admin.dashboard.activeAlerts.ago', { time: Math.round((Date.now() - alert.timestamp.getTime()) / (1000 * 60)) })}
                </div>
              </div>
              <AlertTriangle className="w-4 h-4 text-orange-600 flex-shrink-0" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Main Admin Dashboard Grid
export const AdminDashboardGrid: React.FC<{
  isMobile?: boolean;
}> = ({ isMobile = false }) => {
  const { t } = useTranslation();
  const { employees } = useEmployeeStore();
  const { auditLogs } = useAdminStore();
  
  // todayRevenue / todayOrders have no endpoint yet — they stay null and
  // render as "—". A hardcoded 3247.89 multiplied by a stale 85 KGS rate is
  // the kind of number that ends up in a customer demo.
  //
  // activeStaff and pendingActions are derived from stores that also have no
  // loader: employeeStore.employees and adminStore.auditLogs both start []
  // and nothing fetches them. Counting an empty list gives 0, which reads as
  // "nobody is on shift" rather than "we never asked" — so they are null too
  // until something populates them.
  const hasStaffData = employees.length > 0;
  const hasAuditData = auditLogs.length > 0;

  const businessMetrics = {
    todayRevenue: null as number | null,
    todayOrders: null as number | null,
    activeStaff: hasStaffData ? employees.filter(e => e.isActive).length : null,
    pendingActions: hasAuditData
      ? auditLogs.filter(l => l.requiresSecondaryApproval && !l.approvedBy).length
      : null,
  };

  if (isMobile) {
    return (
      <div className="space-y-4 px-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-gray-900">{t('admin.dashboard.title')}</h2>
            <p className="text-sm text-gray-600">{t('admin.dashboard.subtitle')}</p>
          </div>
          <Smartphone className="w-6 h-6 text-blue-500" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          {/* No icon: the currency is som, so a $ glyph was actively wrong, and
              the formatted value already states its unit. Same call as the
              super-admin dashboard's revenue card. */}
          <QuickStatsCard
            title={t('admin.dashboard.todayRevenue')}
            value={businessMetrics.todayRevenue != null ? formatKyrgyzCurrency(businessMetrics.todayRevenue) : '—'}
            color="green"
          />
          <QuickStatsCard
            title={t('admin.dashboard.ordersProcessed')}
            value={businessMetrics.todayOrders ?? '—'}
            icon={ShoppingBag}
            color="blue"
          />
        </div>
        <KitchenMonitorWidget variant="compact" showHeader={false} />
        <PerformanceAlerts t={t} />

        {/*
          Only reachable on a narrow viewport — this whole branch is behind
          isMobile — which is the one place where pointing at the desktop
          dashboard means anything. It used to sit in the desktop header
          instead, telling people already on a desktop to switch to it.
        */}
        <div className="flex items-center justify-center space-x-2 pb-2 text-xs text-gray-500">
          <Monitor className="w-4 h-4 flex-shrink-0" />
          <span>{t('admin.dashboard.desktopView')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Desktop Header */}
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-gray-900 flex items-center">
          <LayoutDashboard className="w-7 h-7 mr-3 text-blue-500" />
          {t('admin.dashboard.title')}
        </h2>
        <p className="text-gray-600 mt-1">{t('admin.dashboard.subtitle')}</p>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
        {/* No icon: the currency is som, so a $ glyph was actively wrong, and
            the formatted value already states its unit. Same call as the
            super-admin dashboard's revenue card. */}
        <QuickStatsCard
          title={t('admin.dashboard.todayRevenue')}
          value={businessMetrics.todayRevenue != null ? formatKyrgyzCurrency(businessMetrics.todayRevenue) : '—'}
          color="green"
        />
        <QuickStatsCard
          title={t('admin.dashboard.ordersProcessed')}
          value={businessMetrics.todayOrders ?? '—'}
          icon={ShoppingBag}
          color="blue"
        />
        {/* "Все на месте" used to sit on this card unconditionally — including
            over a staff count of 0, which is the one case where it cannot be
            true. The label only appears when there is a roster to describe. */}
        <QuickStatsCard
          title={t('admin.dashboard.activeStaff')}
          value={businessMetrics.activeStaff ?? '—'}
          change={businessMetrics.activeStaff ? t('admin.dashboard.allPresent') : undefined}
          changeType="positive"
          icon={Users}
          color="purple"
        />
        <QuickStatsCard
          title={t('admin.dashboard.pendingActions')}
          value={businessMetrics.pendingActions ?? '—'}
          change={
            businessMetrics.pendingActions == null
              ? undefined
              : businessMetrics.pendingActions > 0
                ? t('admin.dashboard.needsAttention')
                : t('admin.dashboard.allClear')
          }
          changeType={businessMetrics.pendingActions ? 'negative' : 'positive'}
          icon={AlertTriangle}
          color={businessMetrics.pendingActions ? 'red' : 'gray'}
        />
      </div>

      {/* Main Dashboard Sections */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <KitchenMonitorWidget variant="expanded" />
        </div>
        <div className="space-y-6">
          <PerformanceAlerts t={t} />
        </div>
      </div>

      {/* Bottom Section */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <RecentActivityFeed t={t} />
        <div className="bg-white rounded-xl shadow-soft border border-neutral-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <BarChart3 className="w-5 h-5 mr-2 text-indigo-500" />
              {t('admin.dashboard.performanceTrends.title')}
            </h3>
            <span className="text-sm text-gray-500">{t('admin.dashboard.performanceTrends.last7Days')}</span>
          </div>
          <div className="h-32 bg-ink-50 rounded-lg flex items-center justify-center">
            <div className="text-center text-gray-600">
              <BarChart3 className="w-12 h-12 mx-auto mb-2 text-indigo-400" />
              <p className="text-sm">{t('admin.dashboard.performanceTrends.chartTitle')}</p>
              <p className="text-xs text-gray-500">{t('admin.dashboard.performanceTrends.chartSubtitle')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};